/**
 * The handles on a placement, and the gestures that drag them.
 *
 * ## Where it sits
 *
 * A canvas beside the artwork, INSIDE the zoom wrapper — the same place the
 * symmetry and footprint overlays live. That is what keeps the outline welded
 * to the artwork at every zoom without a line of code: the wrapper's transform
 * moves both together.
 *
 * The handles themselves must NOT scale, or a 7px grab target becomes 224px at
 * 32x and the outline disappears under its own decoration. So everything drawn
 * here divides its size by the current view scale, which cancels the transform
 * exactly and leaves a handle the same size under the finger at any zoom. The
 * same number divides the hit tolerance, for the same reason.
 *
 * ## How it gets the pointer without racing anybody
 *
 * The overlay is `pointer-events: none`. It has to be: it covers the whole
 * template, and a transparent sheet over the canvas that swallowed the first
 * millimetre of every stroke would be far worse than no handles.
 *
 * `tryBegin` is offered to the shell instead, which calls it as one more early
 * return in the painter's own pointerdown. That is the convention this codebase
 * arrived at after the face picker tried capture-phase interception: one early
 * return beats two listeners racing for the same event, and every input path
 * added later inherits it.
 *
 * It also matters for touch. Stopping the event here would hide it from the
 * view controller, so a pinch that began on a handle would never be recognised
 * — the second finger would arrive to a viewport that had never seen the first.
 */
import { CANVAS, type FacePlacement } from "../../shared/format";
import { cornersOf, fitToFace, resolvePlacement } from "../../shared/placement";
import type { LayerStack, RuntimeLayer } from "./layers";
import { gripOf, type Handle, hitTest, movedBy, rotatedTo, scaledTo } from "./transform";

const STYLE_ID = "transform-overlay-style";

const CSS = `
.tf-overlay {
  position: absolute;
  left: 0;
  top: 0;
  /* Never in the way of a stroke. The gestures listen on the canvas instead. */
  pointer-events: none;
  z-index: 2;
}
`;

/** Handle square, in SCREEN pixels. Divided by the zoom before drawing. */
const HANDLE_PX = 9;
/** Grab radius, in screen pixels. Larger than the handle so it is not fiddly. */
const GRAB_PX = 14;

export interface TransformOverlayOptions {
  /** The artwork canvas. Its parent is the zoom wrapper. */
  canvas: HTMLCanvasElement;
  stack: LayerStack;
  /** Current zoom, so handles can cancel it out. 1 when there is no view. */
  viewScale: () => number;
  /** Client coordinates to template pixels — the shell's own `toTemplate`. */
  toTemplate: (e: PointerEvent) => { x: number; y: number };
  /**
   * One finished gesture, with the placement either side of it.
   *
   * Fired on pointer-up and only when something actually changed, so the
   * caller writes one history entry per drag rather than one per frame.
   */
  onCommit: (layerId: string, before: FacePlacement, after: FacePlacement) => void;
  doc?: Document;
}

export interface TransformOverlay {
  /** Redraw. Called when the stack changes or the view moves. */
  refresh(): void;
  /**
   * Take this press if it lands on a handle. True means the caller should stop.
   * Offered to the shell rather than intercepted, so nothing races the painter.
   */
  tryBegin(e: PointerEvent): boolean;
  /** True while a handle is being dragged, so other tools can stand aside. */
  dragging(): boolean;
  /**
   * Abandon the drag and put the placement back where it started.
   *
   * What a second finger triggers: the gesture is a pinch now, and a
   * half-finished drag committed on the way into a zoom is the same smear the
   * painter's `cancel` exists to prevent.
   */
  cancel(): void;
  /** Move the selected placement by whole template pixels. Returns false when
   *  there is nothing selected to nudge. */
  nudge(dx: number, dy: number): boolean;
  destroy(): void;
}

export function mountTransformOverlay(opts: TransformOverlayOptions): TransformOverlay {
  const doc = opts.doc ?? document;
  const stage = opts.canvas.parentElement;
  if (!stage) throw new Error("transform overlay: the canvas has no stage to sit in");

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  const el = doc.createElement("canvas");
  el.className = "tf-overlay";
  el.setAttribute("aria-hidden", "true");
  el.width = CANVAS.width;
  el.height = CANVAS.height;
  el.style.width = "100%";
  el.style.height = "100%";
  stage.appendChild(el);
  const ctx = el.getContext("2d");

  /** The placement layer being transformed, or null when none is selected. */
  function selected(): RuntimeLayer | null {
    const layer = opts.stack.active();
    if (layer?.kind !== "placement" || !layer.placement) return null;
    /*
      Nothing to grab on a layer you cannot see. Handles on a hidden placement
      are worse than no handles: the outline moves, the artwork does not, and
      the only feedback is an undo entry for something invisible.
    */
    return layer.visible ? layer : null;
  }

  const aspectOf = (layer: RuntimeLayer) =>
    layer.source.height > 0 ? layer.source.width / layer.source.height : 1;

  // ------------------------------------------------------------------ draw

  function refresh() {
    if (!ctx) return;
    ctx.clearRect(0, 0, el.width, el.height);
    const layer = selected();
    if (!layer?.placement) return;

    const scale = Math.max(0.0001, opts.viewScale());
    // Cancel the wrapper's transform, so a handle is HANDLE_PX on screen.
    const handle = HANDLE_PX / scale;
    const line = 1.5 / scale;

    const rect = resolvePlacement(layer.placement, aspectOf(layer));
    const corners = cornersOf(rect);
    const grip = gripOf(rect);

    /*
      Warn by colour rather than by words.

      `fitToFace` already knows whether this hangs off its own face — which
      means part of it will be clipped away on export and the rest will look
      fine on the template. A red outline says it at the moment it happens,
      where a toast would arrive after the gesture and be dismissed.
    */
    const fits = fitToFace(layer.placement, aspectOf(layer)).fits;
    const ink = fits ? "#2F8AFF" : "#E2231A";

    ctx.save();
    ctx.strokeStyle = ink;
    ctx.lineWidth = line;

    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (const c of corners.slice(1)) ctx.lineTo(c.x, c.y);
    ctx.closePath();
    ctx.stroke();

    // The stalk out to the rotation grip, so it reads as attached.
    const top = { x: (corners[0].x + corners[1].x) / 2, y: (corners[0].y + corners[1].y) / 2 };
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.lineTo(grip.x, grip.y);
    ctx.stroke();

    ctx.fillStyle = "#fff";
    for (const c of corners) {
      ctx.beginPath();
      ctx.rect(c.x - handle / 2, c.y - handle / 2, handle, handle);
      ctx.fill();
      ctx.stroke();
    }
    // A round grip, so it does not read as a fifth corner.
    ctx.beginPath();
    ctx.arc(grip.x, grip.y, handle / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  // -------------------------------------------------------------- gestures

  interface Drag {
    id: number;
    layerId: string;
    handle: Handle;
    /** The placement as it was when the pointer went down. */
    start: FacePlacement;
    grab: { x: number; y: number };
    aspect: number;
    moved: boolean;
  }
  let drag: Drag | null = null;

  /**
   * Take this press, if it lands on a handle.
   *
   * Returns true when the gesture is ours, which is the shell's cue to stop
   * before it paints.
   */
  function tryBegin(e: PointerEvent): boolean {
    const layer = selected();
    if (!layer?.placement || e.button !== 0) return false;
    const at = opts.toTemplate(e);
    const aspect = aspectOf(layer);
    const scale = Math.max(0.0001, opts.viewScale());
    const handle = hitTest(layer.placement, aspect, at.x, at.y, GRAB_PX / scale);
    if (!handle) return false;

    drag = {
      id: e.pointerId,
      layerId: layer.id,
      handle,
      start: { ...layer.placement },
      grab: at,
      aspect,
      moved: false,
    };
    opts.canvas.setPointerCapture?.(e.pointerId);
    return true;
  }

  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    const layer = opts.stack.get(drag.layerId);
    if (!layer) return;
    const at = opts.toTemplate(e);

    let next: FacePlacement;
    if (drag.handle === "move") {
      next = movedBy(drag.start, at.x - drag.grab.x, at.y - drag.grab.y);
    } else if (drag.handle === "rotate") {
      next = rotatedTo(drag.start, drag.aspect, drag.grab, at, e.shiftKey);
    } else {
      next = scaledTo(drag.start, drag.aspect, drag.grab, at);
    }

    /*
      Through the stack, not by assignment.

      `setPlacement` already recomposites the UNION of where the placement was
      and where it now is — assigning the field directly and composing the new
      area leaves the old one unrepainted, which is a smear trailing the drag.
    */
    opts.stack.setPlacement(drag.layerId, next);
    drag.moved = true;
    refresh();
  };

  const onUp = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    const finished = drag;
    drag = null;
    if (opts.canvas.hasPointerCapture?.(e.pointerId)) {
      opts.canvas.releasePointerCapture(e.pointerId);
    }
    const layer = opts.stack.get(finished.layerId);
    if (!layer?.placement || !finished.moved) return;
    // Only a gesture that changed something is worth an undo step.
    const after = { ...layer.placement };
    const before = finished.start;
    const same =
      before.x === after.x &&
      before.y === after.y &&
      before.scale === after.scale &&
      before.rotation === after.rotation;
    if (!same) opts.onCommit(finished.layerId, before, after);
    refresh();
  };

  /*
    Move and up only. The press arrives through `tryBegin`, and these do
    nothing unless one was taken — so they cannot interfere with a stroke.
  */
  opts.canvas.addEventListener("pointermove", onMove);
  opts.canvas.addEventListener("pointerup", onUp);
  opts.canvas.addEventListener("pointercancel", onUp);

  /*
    No subscription here: the stack reports through the `onChange` it was built
    with, and the shell already calls `renderPanel()` from there. `refresh` is
    exported so it can be called from the same place, rather than this module
    inventing a second notification channel for the same event.
  */
  refresh();

  function cancel() {
    if (!drag) return;
    const finished = drag;
    drag = null;
    // Straight back to the pointerdown value — no history entry, because as far
    // as the document is concerned this gesture never happened.
    opts.stack.setPlacement(finished.layerId, finished.start);
    refresh();
  }

  function nudge(dx: number, dy: number): boolean {
    const layer = selected();
    if (!layer?.placement) return false;
    const before = { ...layer.placement };
    const after = movedBy(before, dx, dy);
    if (after.x === before.x && after.y === before.y) return true;
    opts.onCommit(layer.id, before, after);
    return true;
  }

  return {
    refresh,
    tryBegin,
    cancel,
    nudge,
    dragging: () => drag !== null,
    destroy() {
      opts.canvas.removeEventListener("pointermove", onMove);
      opts.canvas.removeEventListener("pointerup", onUp);
      opts.canvas.removeEventListener("pointercancel", onUp);
      el.remove();
    },
  };
}
